import { useCallback, useMemo, useRef, useState } from 'react';

function normalizeComposerText(value) {
  const raw = String(value || '').replace(/\r\n?/g, '\n');

  const normalizedLines = raw
    .split('\n')
    .map((line) => line.replace(/[ \t]+/g, ' ').trim())
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

  return normalizedLines;
}

export default function useFeedComposer(options = {}) {
  const {
    onSubmit,
    maxLength = 600,
    minLength = 2,
    initialValue = '',
  } = options;

  const initialText = useMemo(() => String(initialValue || ''), [initialValue]);
  const [text, setText] = useState(initialText);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const submitLockRef = useRef(false);

  const normalizedText = useMemo(() => normalizeComposerText(text), [text]);
  const rawLength = useMemo(() => String(text || '').length, [text]);
  const trimmedLength = useMemo(() => normalizedText.length, [normalizedText]);
  const remaining = useMemo(() => Math.max(0, maxLength - rawLength), [maxLength, rawLength]);
  const isEmpty = useMemo(() => trimmedLength === 0, [trimmedLength]);
  const isTooShort = useMemo(
    () => trimmedLength > 0 && trimmedLength < minLength,
    [trimmedLength, minLength]
  );
  const isTooLong = useMemo(() => rawLength > maxLength, [rawLength, maxLength]);

  const canSubmit = useMemo(() => {
    return !submitting && !isEmpty && !isTooShort && !isTooLong && typeof onSubmit === 'function';
  }, [submitting, isEmpty, isTooShort, isTooLong, onSubmit]);

  const reset = useCallback((nextValue = '') => {
    setText(String(nextValue || ''));
    setError('');
  }, []);

  const restoreInitialValue = useCallback(() => {
    setText(initialText);
    setError('');
  }, [initialText]);

  const handleChange = useCallback((valueOrEvent) => {
    const nextValue =
      typeof valueOrEvent === 'string'
        ? valueOrEvent
        : valueOrEvent?.target?.value || '';

    setText(nextValue);

    if (error) {
      setError('');
    }
  }, [error]);

  const submit = useCallback(async (submitOptions = {}) => {
    if (submitting || submitLockRef.current) return null;

    const safeText = normalizeComposerText(text);

   const hasText = !!safeText;
const hasImage = !!submitOptions?.uploadId;

if (!hasText && !hasImage) {
  setError('Agregá texto o una imagen para publicar.');
  return null;
}

    if (safeText.length < minLength) {
      setError(`Tu publicación debe tener al menos ${minLength} caracteres.`);
      return null;
    }

    if (String(text || '').length > maxLength) {
      setError(`Tu publicación no puede superar los ${maxLength} caracteres.`);
      return null;
    }

    if (typeof onSubmit !== 'function') {
      setError('No se pudo publicar en este momento.');
      return null;
    }

    submitLockRef.current = true;
    setSubmitting(true);
    setError('');

    try {
      const result = await onSubmit({
        text: safeText,
        rawText: String(text || ''),
        ...(submitOptions && typeof submitOptions === 'object' ? submitOptions : {}),
      });
      setText('');
      setError('');
      return {
        ok: true,
        text: safeText,
        result,
      };
    } catch (err) {
      const message = err?.message || 'No se pudo publicar.';
      setError(message);
      throw err;
    } finally {
      submitLockRef.current = false;
      setSubmitting(false);
    }
  }, [submitting, text, minLength, maxLength, onSubmit]);

  const onKeyDown = useCallback(
    async (event) => {
      if (!event) return;

      if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
        event.preventDefault();
        await submit();
      }
    },
    [submit]
  );

  return {
    text,
    setText,
    error,
    setError,
    submitting,
    normalizedText,
    rawLength,
    trimmedLength,
    remaining,
    isEmpty,
    isTooShort,
    isTooLong,
    canSubmit,
    handleChange,
    submit,
    reset,
    restoreInitialValue,
    onKeyDown,
  };
}
